import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  CustomerListRequest,
  useFetchCustomersQuery,
} from '../customer-api';
import AuditLogsScreen from '../../../components/common/auditLogs';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
  Space,
  Table,
  Tooltip
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  MinusCircleFilled,
  MoreOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { CustomerViewModel, PaginatedResult } from '../../api/models';
import { DefaultPage, DefaultPageSize, DefaultPollingInterval } from '../../../core/defaults';
import Title from 'antd/lib/typography/Title';
import { getLink } from '../../../App-router';
import { TableState } from '../../../components/table/model';
import getSortBy from '../../../components/table/sorter';
import { useMasterDataDelete } from '../../masterdata/useMasterDataDelete';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function CustomersScreen(props: any) {
  const location = useLocation();
  const query = useQuery();
  const params: any = useParams<any>();
  const navigate = useNavigate();
  const [showAuditLogs, setShowAuditLogs] = useState<boolean>(false);
  const [entityId, setEntityId] = useState<number>(0);

  // Helper function to get sortOrder for a column based on current URL state
  const getSortOrderForColumn = (columnKey: string): 'ascend' | 'descend' | null => {
    const sortBy = query.get('sortBy') || '';
    if (!sortBy) return null;
    
    // Map frontend column key to backend column name
    const columnMapping: { [key: string]: string } = {
      'Id': 'Id',
      'name': 'Name',
    };
    
    const backendColumnName = columnMapping[columnKey] || columnKey;
    
    // Split sortBy to get column name and direction
    const sortByParts = sortBy.trim().split(/\s+/);
    const sortColumnName = sortByParts[0];
    const sortDirection = sortByParts[1]?.toLowerCase() || '';
    
    // Check if the column name matches exactly (case-insensitive)
    if (sortColumnName.toLowerCase() === backendColumnName.toLowerCase()) {
      return sortDirection === 'desc' ? 'descend' : 'ascend';
    }
    return null;
  };

  const customerTable: TableState = {
    columns: [
      {
        title: 'Customer',
        dataIndex: '',
        key: 'Id',
        render: (customer: CustomerViewModel) => (
          <>
              <Space>
                <span>{customer.id}</span>
              </Space>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('Id'),
        fixed: 'left',
      },
      {
        title: 'Name',
        dataIndex: '',
        key: 'name',
        render: (customer: CustomerViewModel) => (
          <>
              <Tooltip title={customer.name} >
            <span>{customer.name}</span>
            </Tooltip>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('name'),
      },
      {
        title: 'Type',
        dataIndex: '',
        key: 'type',
        render: (customer: CustomerViewModel) => (
          <>
              <span>{customer.type}</span>
          </>
        ),
      },
      {
        title: 'Qualifier',
        key: 'qualifier',
        dataIndex: '',
        render: (customer: CustomerViewModel) => (
          <>
              <span>{customer.qualifier}</span>
          </>
        ),
      },      
      {
        title: 'Actions',
        key: 'action',
        dataIndex: '',
        fixed:'right',
        render: (customer: CustomerViewModel) => (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlay={
              <Menu style={{ padding: '8px 8px' }}>
                <Menu.Item icon={<EditOutlined />} key="1">
                  <Link
                    to={getLink('CustomerEdit', {
                      tenantId: params.tenantId,
                      Id: customer.id,
                    })}
                  >
                    Edit
                  </Link>
                </Menu.Item>
                <Menu.Item icon={<InfoCircleOutlined />} key="3">
                <Link to="#" onClick={()=>setShowAuditLogs(true)}>
                    Audit Logs
                  </Link>
                </Menu.Item>
                <Menu.Item
                  icon={<DeleteOutlined />}
                  key="delete"
                  disabled={!!(deletingId && deletingId === customer.id)}
                  onClick={() => deleteEntity(customer.id ?? 0, customer.name ?? '')}
                >
                  {deletingId === customer.id ? 'Deleting...' : 'Delete'}
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined />
          </Dropdown>
        ),
      },
    ],
  };

  const getCurrentState = (): CustomerListRequest => {
    const urlParams = new URLSearchParams(location.search);
    return {
      current: Number(urlParams.get('p')) || DefaultPage,
      pageSize: Number(urlParams.get('size')) || DefaultPageSize,
      filter: urlParams.get('q'),
      sortBy: urlParams.get('sortBy') || 'EnteredByDate Desc',
    };
  };

  const [pagination, setPagination] = useState<CustomerListRequest>(getCurrentState());

  const {
    data: pagedData = {},
    isFetching,
    error,
  }: {
    data?: PaginatedResult<CustomerViewModel> | undefined;
    isFetching?: boolean | undefined;
    error?: any;
  } = useFetchCustomersQuery(pagination, { pollingInterval: DefaultPollingInterval });

  // Update pagination when URL query params change (including page number)
  useEffect(() => {
    const newState = getCurrentState();
    setPagination(newState);
  }, [location.search]);

  const {
    deletingId,
    isDeleting: isDeleteLoading,
    deleteError,
    deleteErrorMessage,
    deleteEntity,
  } = useMasterDataDelete({
    entityType: 'Customers',
    tagType: 'Customer',
    entityLabel: 'customer',
  });
  const { confirm } = Modal;

  const onCreateNew = useCallback(
    () => navigate(getLink('CustomerEdit', { ...params, Id: 'new' })),
    [params, navigate]
  );

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<CustomerViewModel> | SorterResult<CustomerViewModel>[],
    extra: TableCurrentDataSource<CustomerViewModel>
  ) => {
    // Handle sorter - it can be null, undefined, a single object, or an array
    const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
    const { columnKey, order } = (sorterResult || {}) as SorterResult<CustomerViewModel>;

    // Build new sort parameter
    const defaultSort = 'EnteredByDate Desc';
    // Read current sort from URL to ensure fresh value
    const urlParams = new URLSearchParams(location.search);
    const currentSortBy = urlParams.get('sortBy') || defaultSort;
    const currentPage = Number(urlParams.get('p')) || 1;
    
    // Determine new page number - ensure it's always a valid number
    let newPage = paginationConfig.current;
    if (!newPage || newPage < 1) {
      newPage = currentPage;
    }
    
    // Determine new sort - always ensure it's a valid string
    let newSortBy: string = defaultSort;
    
    // Check if we're actually changing sort or just pagination
    const isSortingAction = columnKey && order; // User clicked a column header with sort direction
    const isCancellingSort = columnKey && !order && currentSortBy !== defaultSort; // User cancelled an active sort
    const isPaginationOnly = !columnKey || (!order && currentSortBy === defaultSort); // Just pagination, sort already default

    // Handle sorting: Ant Design cycles through: null -> ascend -> descend -> null
    if (isSortingAction) {
      // Order is present (ascend or descend) - user is actively sorting
      const columnMapping: { [key: string]: string } = {
        'Id': 'Id',
        'name': 'Name',
      };
      
      const clickedColumn = columnKey.toString();
      const backendColumnName = columnMapping[clickedColumn] || clickedColumn;
      newSortBy = getSortBy(backendColumnName, order);
      
      // Reset to page 1 on sort change
      if (newSortBy !== currentSortBy) {
        newPage = 1;
      }
    } else if (isCancellingSort) {
      // Order is null/undefined AND we had an active sort - user is cancelling sort
      newSortBy = defaultSort;
      newPage = 1;
    } else {
      // Pagination only - preserve current sort and use the page number from paginationConfig
      newSortBy = currentSortBy || defaultSort;
      // newPage is already set from paginationConfig.current above
    }
    
    // Final safety check - ensure newSortBy is never empty
    if (!newSortBy || newSortBy.trim() === '') {
      newSortBy = defaultSort;
    }

    // Ensure pageSize is valid
    const newPageSize = paginationConfig.pageSize || Number(urlParams.get('size')) || 20;
    
    // Build query parameters object
    const queryParams: any = {
      sortBy: newSortBy,
      p: newPage,
      size: newPageSize,
    };
    
    // Add optional parameters only if they have values
    const qValue = urlParams.get('q');
    if (qValue) {
      queryParams.q = qValue;
    }
    
    const newUrl = getLink(
      'CustomerList',
      { ...params },
      queryParams
    );
    
    // Always navigate - React Router will handle if URL is the same
    navigate(newUrl, { replace: false });
  };

  const handleSearch = (searchText: string) => {
    navigate(
      getLink(
        'CustomerList',
        { ...params },
        {
          p: DefaultPage,
          size: pagination.pageSize,
          q: searchText?.length ? `Name:*${searchText.replaceAll(" ", "\\ ")}*` : '',
          sortBy: pagination.sortBy || '',  // Preserve current sort
        }
      )
    );
  };


  return (
    <div>
      <PageHeader
        extra={
          <Button htmlType="button" data-testid={'create-new-customer'} onClick={onCreateNew}>
            New Customer
          </Button>
        }
        title={
          <Title
            level={4}
            style={{
              marginBottom: 0,
              display: 'inline-block',
            }}
          >
            Customers
          </Title>
        }
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={getLink('home', { tenantId: params.tenantId })}>
              <HomeOutlined />
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>              
              <Link to={getLink('cob', { tenantId: params.tenantId })}>
              Dashboard
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item key="customers">
              <Link to={getLink('CustomerList', { tenantId: params.tenantId })}>Customers</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
      >
        <Card>
          {error?.status && (
            <Alert message="Error" description={error.data} type="error" showIcon />
          )}
          {deleteError && (
            <Alert message="Error" description={deleteErrorMessage} type="error" showIcon />
          )}
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Input.Search
              placeholder="Search by Name"
              onSearch={handleSearch}
              enterButton
              style={{ width: 400 }}
              loading={isFetching}
              allowClear
            />
            <Table
            scroll={{ x: true }}
              dataSource={pagedData.results}
              onChange={handleTableChange}
              rowKey={'id'}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagedData.totalCount,
                showTotal: (total) => `Total ${total} items`,
                pageSizeOptions: ["10", "20", "50","100"],
                showSizeChanger: true,
                locale: { items_per_page: " / Page" }
              }}
              columns={customerTable.columns}
              loading={isFetching}
              onRow={(record, rowIndex) => {
                return {
                  onClick: event => { 
                    setEntityId(record?.id||0);
                  },
                };
              }}
            />
          </Space>
        </Card>
        {showAuditLogs?<AuditLogsScreen entityId={entityId} entityName={'Customer'} showAuditLogs={setShowAuditLogs}/>:<></>}
      </PageHeader>
    </div>
  );
}
